import React from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '@/redux/Store';
import {
  selectAchievementsByUsername,
  selectAchievementsLoading,
} from '@/redux/selectors/achievementSelectors';
import './AchievementTokens.scss';

const MAX_DISPLAY = 4;

interface Props {
  username: string;
  /** When true, skip the component's own "Achievements / View all"
   *  header and the outer chrome (border, background, padding). Used
   *  inside the about-card grid so the card owns one heading row
   *  instead of stacking two. */
  compact?: boolean;
}

const AchievementTokens: React.FC<Props> = ({ username, compact = false }) => {
  const achievements = useAppSelector(selectAchievementsByUsername(username));
  const loading = useAppSelector(selectAchievementsLoading(username));

  if (loading || achievements.length === 0) return null;

  // Show featured pins first; fall back to most-recently-earned when the user
  // hasn't pinned anything yet.
  const featured = achievements
    .filter((a) => a.featuredOrder !== null)
    .sort((a, b) => (a.featuredOrder ?? 99) - (b.featuredOrder ?? 99));

  const display = featured.length > 0
    ? featured.slice(0, MAX_DISPLAY)
    : [...achievements]
        .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
        .slice(0, MAX_DISPLAY);

  const linkUsername = username.startsWith('@') ? username : `@${username}`;

  return (
    <div className={`achievement-tokens${compact ? ' achievement-tokens--compact' : ''}`}>
      {!compact && (
        <div className="achievement-tokens__header">
          <span className="achievement-tokens__label">Achievements</span>
          <Link to={`/achievements/${linkUsername}`} className="achievement-tokens__toggle">
            View all →
          </Link>
        </div>
      )}
      <div className="achievement-tokens__grid">
        {display.map((ach) => (
          <div key={ach.type} className="achievement-token" title={ach.description}>
            <span className="achievement-token__icon">{ach.icon}</span>
            <span className="achievement-token__name">{ach.name}</span>
            <span className="achievement-token__date">
              {new Date(ach.awardedAt).toLocaleDateString(undefined, {
                month: 'short',
                year: 'numeric',
              })}
            </span>
            {ach.txHash && (
              <span className="achievement-token__verified" title="On-chain verified">✓</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AchievementTokens;
